import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { AuditLogActionFilter } from '@/frontend/components/AuditLogActionFilter'

interface FilterOption {
  id: string
  name: string
}

interface Props {
  filters: {
    organizations: FilterOption[]
    digital_workers: FilterOption[]
    users: FilterOption[]
  }
  search: string
  organization_id: string
  excluded_actions: string[]
  digital_worker_id: string
  user_id: string
  date_from: string
  date_to: string
  onSubmit: (page: number, overrides?: Record<string, unknown>) => void
}

export default function AuditLogFilters({ filters, search, organization_id, excluded_actions, digital_worker_id, user_id, date_from, date_to, onSubmit }: Props) {
  const [query, setQuery] = useState(search)

  useEffect(() => {
    setQuery(search)
  }, [search])

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6 space-y-3">
      <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
        <SearchableSelect
          ariaLabel="Filtrar por organização"
          value={organization_id}
          onValueChange={(orgId) => onSubmit(1, { organization_id: orgId, digital_worker_id: '', user_id: '' })}
          options={filters.organizations.map((org) => ({ value: org.id, label: org.name }))}
          placeholder="Organização"
          clearLabel="Todas as organizações"
          searchPlaceholder="Buscar organização..."
        />

        <AuditLogActionFilter
          excludedActions={excluded_actions}
          onExcludedActionsChange={(excluded) => onSubmit(1, { excluded_actions: excluded })}
        />

        <select
          aria-label="Filtrar por digital worker"
          disabled={!organization_id}
          value={digital_worker_id}
          onChange={(e) => onSubmit(1, { digital_worker_id: e.target.value })}
          className="px-3 py-2 text-sm border border-border rounded-lg disabled:bg-muted disabled:text-muted-foreground"
        >
          <option value="">Digital Worker</option>
          {filters.digital_workers.map((worker) => (
            <option key={worker.id} value={worker.id}>{worker.name}</option>
          ))}
        </select>

        <select
          aria-label="Filtrar por ator"
          disabled={!organization_id}
          value={user_id}
          onChange={(e) => onSubmit(1, { user_id: e.target.value })}
          className="px-3 py-2 text-sm border border-border rounded-lg disabled:bg-muted disabled:text-muted-foreground"
        >
          <option value="">Ator</option>
          {filters.users.map((user) => (
            <option key={user.id} value={user.id}>{user.name}</option>
          ))}
        </select>

        <div className="flex gap-2">
          <input
            type="date"
            aria-label="Data inicial"
            value={date_from}
            onChange={(e) => onSubmit(1, { date_from: e.target.value })}
            className="px-3 py-2 text-sm border border-border rounded-lg flex-1 min-w-0"
          />

          <input
            type="date"
            aria-label="Data final"
            value={date_to}
            onChange={(e) => onSubmit(1, { date_to: e.target.value })}
            className="px-3 py-2 text-sm border border-border rounded-lg flex-1 min-w-0"
          />
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          onSubmit(1, { search: query })
        }}
        className="flex items-center gap-2"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            aria-label="Buscar eventos de auditoria"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por comando, ferramenta, conteúdo..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
        <Button type="submit" variant="outline" size="sm">
          Buscar
        </Button>
      </form>
    </div>
  )
}
